import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "./Reveal";

export const Waitlist = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "enroll", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/signups", form);
      setDone(true);
      toast.success("You're on the list! We'll be in touch soon. 👑");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enroll" data-testid="waitlist-section" className="bg-wine py-24 sm:py-32 relative grain overflow-hidden">
      <div className="pointer-events-none absolute -bottom-40 -left-20 h-[500px] w-[500px] rounded-full bg-gold/10 blur-[130px]" />
      <div className="relative z-10 max-w-[1100px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
            Secure Your Place
          </span>
          <h2 className="font-display font-light text-cream text-5xl sm:text-6xl leading-[1] mt-5">
            Your crown is <span className="italic text-gold">waiting.</span>
          </h2>
          <p className="text-cream/65 mt-6 text-lg leading-relaxed max-w-md">
            Join the Crowned™ Pro Level 1 waitlist to lock in early-crown pricing and be first in line
            when doors open September 15. Only 5 seats.
          </p>
          <div className="flex items-center gap-3 mt-8 text-cream/60">
            <Crown className="h-5 w-5 text-gold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em]">
              This is more than training. This is transformation.
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="waitlist-success"
              className="rounded-3xl border border-gold/30 bg-wine-deep/70 p-10 text-center"
            >
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold">
                <Check className="h-8 w-8 text-wine-deep" strokeWidth={3} />
              </div>
              <h3 className="font-display text-4xl text-cream mt-6">You're crowned.</h3>
              <p className="text-cream/60 mt-3">
                Welcome to the list. Watch your inbox — enrollment details and your early-crown offer
                are on the way.
              </p>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="waitlist-form"
              className="rounded-3xl border border-gold/20 bg-wine-deep/60 backdrop-blur p-8 sm:p-10 space-y-5"
            >
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">Full Name</label>
                <input
                  data-testid="input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="mt-2 w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors duration-300"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">Email</label>
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="mt-2 w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors duration-300"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">Phone (optional)</label>
                <input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 000-0000"
                  className="mt-2 w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors duration-300"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">I'm interested in</label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {[
                    { v: "enroll", l: "Enrolling now" },
                    { v: "waitlist", l: "Joining waitlist" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`interest-${o.v}`}
                      onClick={() => setForm((f) => ({ ...f, interest: o.v }))}
                      className={`rounded-xl border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                        form.interest === o.v
                          ? "bg-gold text-wine-deep border-gold"
                          : "border-cream/15 text-cream/70 hover:border-gold/50"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                data-testid="waitlist-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reserve My Seat"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
